import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import { http } from "utils";
import { showMessage } from "store/snackBarSlice";

const DASH = "—";

// Filters run on the app's own userPaymentStatus (which the nightly cron keeps
// in step with Stripe) so they apply before pagination. Defaults to Active,
// since the great majority of accounts have lapsed.
const STATUS_FILTERS = [
  { value: "active", label: "Active", countKey: "active" },
  { value: "cancel", label: "Cancelled", countKey: "cancel" },
  { value: "inActive", label: "Inactive", countKey: "inActive" },
  { value: "unset", label: "No status", countKey: "unset" },
  { value: "all", label: "All", countKey: "__all__" },
];

const fmtDate = (value) => {
  if (!value) return DASH;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return DASH;
  return d.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const planLabel = (row) => {
  if (row.isComplimentary) return "Complimentary";
  if (!row.planType) return DASH;
  const price = row.planPrice ? ` · $${row.planPrice}` : "";
  return `Plan ${row.planType}${price}`;
};

// Stripe's states, mapped to something a human scanning the table can act on.
const STATUS_STYLE = {
  active: { label: "Active", color: "success" },
  trialing: { label: "Trial", color: "info" },
  past_due: { label: "Past due", color: "error" },
  unpaid: { label: "Unpaid", color: "error" },
  incomplete: { label: "Incomplete", color: "warning" },
  incomplete_expired: { label: "Expired", color: "default" },
  canceled: { label: "Cancelled", color: "default" },
  unknown: { label: "Unknown", color: "warning" },
};

const StatusChip = ({ row }) => {
  if (row.isComplimentary) {
    return <Chip size="small" label="Complimentary" color="info" variant="outlined" />;
  }
  if (!row.subscriptionStatus) {
    return <Chip size="small" label="No subscription" variant="outlined" />;
  }
  const s = STATUS_STYLE[row.subscriptionStatus] || {
    label: row.subscriptionStatus,
    color: "default",
  };
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      <Chip size="small" label={s.label} color={s.color} />
      {row.cancelAtPeriodEnd && (
        <Chip size="small" label="ending" variant="outlined" color="warning" />
      )}
    </Box>
  );
};

const Clinicians = () => {
  const dispatch = useDispatch();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("active");
  const [statusCounts, setStatusCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(
    (opts = {}) => {
      const nextPage = opts.page !== undefined ? opts.page : page;
      const perPage = opts.rowsPerPage !== undefined ? opts.rowsPerPage : rowsPerPage;
      const term = opts.search !== undefined ? opts.search : search;
      const st = opts.status !== undefined ? opts.status : status;

      setLoading(true);
      http("POST", "admin/users/list", {
        params: { skip: nextPage * perPage, limit: perPage, search: term, status: st },
      })
        .then((res) => {
          setRows(res.data || []);
          setTotal(res.total || 0);
          setStatusCounts(res.statusCounts || {});
        })
        .catch((err) =>
          dispatch(
            showMessage({
              message: err.message || "Could not load clinicians",
              type: "error",
            })
          )
        )
        .finally(() => setLoading(false));
    },
    [dispatch, page, rowsPerPage, search, status]
  );

  useEffect(() => {
    load({ page: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Search is debounced so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(0);
      load({ page: 0, search });
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleLoginAs = (row) => {
    // Open the tab now, inside the click's user-activation window, and
    // navigate it once the handoff URL comes back. Opening it from the .then()
    // is well past that window and gets silently swallowed by the popup
    // blocker.
    const tab = window.open("", "_blank");
    if (tab) {
      tab.document.write(
        "<!DOCTYPE html><html><head><title>Opening session…</title></head>" +
          '<body style="font-family:sans-serif;display:flex;align-items:center;' +
          'justify-content:center;height:100vh;margin:0;color:#555;">' +
          "<p>Opening session…</p></body></html>"
      );
    }

    setBusyId(row._id);
    http("POST", "admin/users/impersonate", { params: { userId: row._id } })
      .then((res) => {
        const url = res.data && res.data.handoffUrl;
        if (!url) throw new Error(res.message || "No session URL returned");
        if (tab) tab.location.href = url;
        else window.location.href = url;
      })
      .catch((err) => {
        if (tab) tab.close();
        dispatch(
          showMessage({
            message: err.message || "Could not start session",
            type: "error",
          })
        );
      })
      .finally(() => setBusyId(null));
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h6">Clinicians</Typography>
          <Typography variant="body2" color="text.secondary">
            {total} {status === "all" ? "" : STATUS_FILTERS.find((f) => f.value === status).label.toLowerCase() + " "}{total === 1 ? "account" : "accounts"}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <TextField
            select
            size="small"
            label="Subscription"
            value={status}
            onChange={(e) => {
              const next = e.target.value;
              setStatus(next);
              setPage(0);
              load({ page: 0, status: next });
            }}
            sx={{ minWidth: 190 }}
          >
            {STATUS_FILTERS.map((f) => (
              <MenuItem key={f.value} value={f.value}>
                {f.label}
                {statusCounts[f.countKey] !== undefined
                  ? ` (${statusCounts[f.countKey]})`
                  : f.value === "all"
                  ? ` (${Object.values(statusCounts).reduce((a, b) => a + b, 0)})`
                  : ""}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label="Search name, clinic or email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 280 }}
          />
        </Box>
      </Box>

      <TableContainer sx={{ overflowX: "auto" }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Clinician</TableCell>
              <TableCell>Clinic</TableCell>
              <TableCell>Plan</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Next billing</TableCell>
              <TableCell>Card</TableCell>
              <TableCell>Last activity</TableCell>
              <TableCell align="right">Troubleshoot</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                  <CircularProgress size={26} />
                </TableCell>
              </TableRow>
            )}

            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                  <Typography variant="body2" color="text.secondary">
                    {search
                      ? `No clinicians match “${search}”.`
                      : "No clinicians yet."}
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {!loading &&
              rows.map((row) => (
                <TableRow key={row._id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {row.username || DASH}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {row.email}
                    </Typography>
                  </TableCell>
                  <TableCell>{row.clinicname || DASH}</TableCell>
                  <TableCell>{planLabel(row)}</TableCell>
                  <TableCell><StatusChip row={row} /></TableCell>
                  <TableCell sx={{ fontVariantNumeric: "tabular-nums" }}>
                    {fmtDate(row.nextBillingDate)}
                  </TableCell>
                  <TableCell sx={{ fontVariantNumeric: "tabular-nums" }}>
                    {row.cardLast4
                      ? `•••• ${row.cardLast4}${row.cardExpiry ? ` · ${row.cardExpiry}` : ""}`
                      : DASH}
                  </TableCell>
                  <TableCell sx={{ fontVariantNumeric: "tabular-nums" }}>
                    {fmtDate(row.lastActivity)}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={busyId === row._id}
                      onClick={() => handleLoginAs(row)}
                    >
                      {busyId === row._id ? "Opening…" : "Login as"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[25, 50, 100]}
        onPageChange={(e, next) => {
          setPage(next);
          load({ page: next });
        }}
        onRowsPerPageChange={(e) => {
          const perPage = parseInt(e.target.value, 10);
          setRowsPerPage(perPage);
          setPage(0);
          load({ page: 0, rowsPerPage: perPage });
        }}
      />

      <Typography variant="caption" color="text.secondary">
        “Login as” opens a 30-minute session in that clinician’s account for
        troubleshooting. Every use is recorded in the impersonation audit log.
      </Typography>
    </Paper>
  );
};

export default Clinicians;
